import { useCallback, useMemo, useState } from 'react';
import axios, { type AxiosRequestConfig } from 'axios';
import { useDebounce } from '@/hooks/useDebounce';
import { useUser } from '@/queries/useUser';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { stanNotify } from '@/lib/stanNotify';
import { useGetUnsplashImageAPICompliance, useUnsplashImages } from './StanUnsplashImageSelector.query';
import type { UnsplashImage } from '@/lib/api/unsplash/types';

export const downloadImageFromUrl = async (url: string): Promise<File> => {
    const responseOptions: AxiosRequestConfig<unknown> = { responseType: 'blob', timeout: 30000 };
    const response = await axios.get<File>(url, responseOptions);
    const file = response.data;

    const type = file.type.split('/');
    const fileName = `image-${Date.now()}.${type[1]}`;

    return new File([file], fileName, { type: file.type });
};

export const useUnsplashImageSelector = (onImagePicked?: (file: File) => void) => {
    const { user } = useUser();
    const [search, setSearch] = useState('');
    const [choosingImage, setChoosingImage] = useState(false);
    const [isLoadingUnsplashImage, setIsLoadingUnsplashImage] = useState(false);

    const debouncedSearch = useDebounce(search, 1000);

    // Use the infinite query hook
    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useUnsplashImages(debouncedSearch);

    // Use the mutation hook for download event
    const { mutateAsync: getUnsplashImageAPICompliance } = useGetUnsplashImageAPICompliance();

    // Flatten all pages into a single array of images
    const images = useMemo(() => {
        const allImages = data?.pages.flatMap(page => page.results) ?? [];
        return allImages.slice(0, allImages.length - 1);
    }, [data]);

    const init = useCallback(() => {
        setSearch('');
        setChoosingImage(false);
    }, []);

    const reset = useCallback(() => {
        setSearch('');
        setChoosingImage(false);
        setIsLoadingUnsplashImage(false);
    }, []);

    const handleNextPage = useCallback(() => {
        if (isLoading || isFetchingNextPage || !hasNextPage) return;
        fetchNextPage();
    }, [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

    const handleImagePicked = async (url: string) => {
        setIsLoadingUnsplashImage(true);

        try {
            const file = await downloadImageFromUrl(url);
            setIsLoadingUnsplashImage(false);
            onImagePicked?.(file);
        } catch (error) {
            setIsLoadingUnsplashImage(false);
            stanNotify({ type: 'error', title: 'Uh oh!', text: 'Failed to download image, please try again.' });
            throw error;
        }
    };

    const handleImageSelected = async (image: UnsplashImage) => {
        setChoosingImage(true);

        try {
            // Trigger download event for Unsplash API compliance
            await getUnsplashImageAPICompliance(image.links.download_location);

            await handleImagePicked(image.urls.regular);

            stanAnalytics('unsplash-image-picked', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: { unsplash_id: image.id },
            });
        } catch (error) {
            console.error('Failed to trigger Unsplash download event:', error);
            stanNotify({ type: 'error', title: 'Uh oh!', text: 'Failed to get image, please try again.' });
        }
    };

    const handleVisitUser = (image: UnsplashImage) => {
        window.open(`${image.user.links.html}?utm_source=Stan&utm_medium=referral`);
    };

    const loading = isLoading || isFetchingNextPage;

    return {
        search,
        setSearch,
        choosingImage,
        setChoosingImage,
        isLoadingUnsplashImage,
        images,
        loading,
        isFetchingNextPage,
        hasNextPage,
        init,
        reset,
        handleNextPage,
        handleImageSelected,
        handleVisitUser,
    };
};
